import { Box, IconButton, Menu, MenuButton, MenuDivider, MenuList, Text } from '@chakra-ui/react'
import {
  faCog,
  faCopy,
  faEllipsisH,
  faFlag,
  faHandPaper,
  faTimes,
  faUserMinus,
} from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { DateTime } from 'luxon'
import { useCallback, useMemo, useState } from 'react'

import type { SeriesPageData } from '@/common/services/seriesPage/types'
import chakraTokenToVar from '@/utils/chakraTokenToVar'

import { useUser } from '@/client/context/user'
import { useServerNow } from '@/client/hooks'
import { RPCRefresh } from '@/client/rpc'
import mutateAndRefreshPage from '@/client/services/trpcMutations'
import { getAdminUrl } from '@/client/services/url'
import { hasUserRole } from '@/client/services/user'
import { useTRPCClient } from '@/client/trpc'

import type { HostToastInfo } from '@/components/Host/CohostReviewModal/types'
import HostTable from '@/components/Host/HostTable'
import { hostAvatarDetailsFromData } from '@/components/Host/helpers'
import type { HostAvatarDetails } from '@/components/Host/types'
import useCohostCancelRequestModal from '@/components/Host/useCohostCancelRequestModal'
import useCohostRemoveSelfModal from '@/components/Host/useCohostRemoveSelfModal'
import useCohostRequestModal from '@/components/Host/useCohostRequestModal'
import ReportModalV2 from '@/components/Report/ReportModalV2'
import ShareMenuGroup from '@/components/Share/ShareMenuGroup'
import ShwLink from '@/components/ShwLink'
import { useToaster } from '@/components/Toast/useToaster'
import Form from '@/components/UI/Form/Form'
import type { FormInputType, FormValues } from '@/components/UI/Form/types'
import MenuItem from '@/components/UI/MenuItem'
import ModalV3 from '@/components/UI/Modal/ModalV3'
import Tippy from '@/components/UI/Tippy'

type MoreOptionsButtonParams = {
  type: 'session' | 'series'
  tutorId: number
  typeId: number
  shareOptions: {
    url: string
    description: string
    shortDescription: string
  }
  duplicateOptions?: {
    url: string
  }
  // Callers supply the assembled details: converted pages map their typed payload
  // (moreOptionsDetailsFromSeriesData), pages still on the tape map their query at the
  // call site (e.g. sessionMoreOptionsDetailsFromQuery next to SessionDetail).
  details: MoreOptionsDetails
  iconColor?: string
}

export default function MoreOptionsButton({
  details,
  type,
  tutorId,
  typeId,
  shareOptions,
  duplicateOptions,
  iconColor,
}: MoreOptionsButtonParams): React.ReactElement | null {
  const { url, description, shortDescription } = shareOptions
  const currentUser = useUser()
  const toast = useToaster()
  const trpcClient = useTRPCClient()

  const [showReportModal, setShowReportModal] = useState(false)
  const [CohostRequestModal, showCohostRequestModal] = useCohostRequestModal()
  const [CohostCancelRequestModal, showCohostCancelRequestModal] = useCohostCancelRequestModal()
  const [CohostRemoveSelfModal, showCohostRemoveSelfModal] = useCohostRemoveSelfModal()
  const [removeCohostInfo, setRemoveCohostInfo] = useState<HostToastInfo | null>(null)
  const [showTableModal, setShowTableModal] = useState(false)

  const isSession = type === 'session'

  const sessionStartDateTime = DateTime.fromISO(details.sessionStartDate)
  const now = useServerNow()
  const sessionHasStarted = sessionStartDateTime.isValid && sessionStartDateTime < now
  const allowsCohosting = details.allowsCohosting
  const isSeriesSession = details.isSeriesSession
  const hasSeriesEnded = details.hasSeriesEnded

  const isUserMainTutor = tutorId === currentUser.id

  const isHost = details.isHost

  const worldId = details.worldId
  const worldTitle = details.worldTitle

  const isTutorInSubworld = details.isTutorInSubworld
  const needsToBeTutorInSubworld = worldId != null && !isTutorInSubworld
  const requestedHost = details.requestedHost
  const hostId = details.hostId

  const mainHostId = details.mainHostId
  const mainHostUserId = details.mainHostUserId
  const mainHostName = details.mainHostName

  const hosts = details.hosts

  const canViewCohostingOption =
    hasUserRole(currentUser, 'user:tutor') &&
    !isUserMainTutor &&
    allowsCohosting &&
    !sessionHasStarted &&
    !isSeriesSession &&
    !hasSeriesEnded

  const canRemoveCohosts =
    (isUserMainTutor || hasUserRole(currentUser, 'admin:super')) &&
    hosts.length > 1 &&
    allowsCohosting &&
    !sessionHasStarted &&
    !isSeriesSession &&
    !hasSeriesEnded

  const removeFormInputs: FormInputType[] = useMemo(
    () => [
      {
        label: 'Write a message (optional)',
        type: 'textarea',
        name: 'reason',
        placeholder: `Let ${removeCohostInfo?.name} know why you are removing them`,
        required: false,
        size: 6,
      },
    ],
    [removeCohostInfo]
  )

  const handleRemove = useCallback(
    async (values: FormValues) => {
      if (removeCohostInfo == null) {
        return
      }
      if (isSession) {
        await RPCRefresh.removeSessionHost({
          sessionHostId: removeCohostInfo.hostId,
          deletedMessage: `${values.reason}`,
        })
      } else {
        await mutateAndRefreshPage(
          trpcClient.series.removeHost.mutate({
            seriesHostId: removeCohostInfo.hostId,
            deletedMessage: `${values.reason}`,
          })
        )
      }
      toast({
        title: `You removed ${removeCohostInfo.name} as a co-host.`,
        status: 'success',
      })
      setRemoveCohostInfo(null)
    },
    [toast, removeCohostInfo, isSession]
  )

  return (
    <>
      <Menu>
        <MenuButton
          as={IconButton}
          variant="icon-outline"
          aria-label="See options"
          icon={
            <FontAwesomeIcon
              icon={faEllipsisH}
              color={iconColor != null ? chakraTokenToVar(iconColor) : undefined}
            />
          }
        />
        <MenuList border={`2px solid ${chakraTokenToVar('border.emphasized')}`}>
          {isHost && duplicateOptions != null ? (
            <Box>
              <ShwLink href={duplicateOptions.url} width="100%">
                <MenuItem value="duplicate" icon={faCopy} label="Duplicate Session" />
              </ShwLink>
            </Box>
          ) : null}

          {canRemoveCohosts ? (
            <MenuItem
              value="removeCohosts"
              icon={faUserMinus}
              label="Remove Co-hosts"
              onClick={() => setShowTableModal(true)}
            />
          ) : null}

          {canViewCohostingOption ? (
            requestedHost ? (
              <MenuItem
                value="cohostRequestCancel"
                icon={faTimes}
                label="Cancel Request to Co-host"
                onClick={showCohostCancelRequestModal}
              />
            ) : isHost ? (
              <MenuItem
                value="removeSelfCohost"
                icon={faUserMinus}
                color="fg.error"
                label="Remove Myself As Co-host"
                onClick={showCohostRemoveSelfModal}
              />
            ) : needsToBeTutorInSubworld ? (
              <Tippy content={`You are not a tutor in the ${worldTitle} subworld.`}>
                <Box>
                  <MenuItem
                    value="cohostRequestDisabled"
                    icon={faHandPaper}
                    label="Request to Co-Host"
                    disabled
                  />
                </Box>
              </Tippy>
            ) : (
              <MenuItem
                value="cohostRequest"
                icon={faHandPaper}
                label="Request to Co-host"
                onClick={showCohostRequestModal}
              />
            )
          ) : null}

          <MenuItem
            value="report"
            icon={faFlag}
            label="Report Tutor"
            onClick={() => setShowReportModal(true)}
          />

          <MenuDivider />

          <ShareMenuGroup url={url} description={description} shortDescription={shortDescription} />

          {hasUserRole(currentUser, 'admin:basic') ||
          hasUserRole(currentUser, 'admin:teach_for_america') ? (
            <>
              <MenuDivider />
              <Box>
                <ShwLink href={getAdminUrl(`user/${tutorId}`)} textDecor="none" width="100%">
                  <MenuItem value="userAdminPage" icon={faCog} label="User Page (Admin Only)" />
                </ShwLink>
              </Box>
              <Box>
                {type === 'series' ? (
                  <ShwLink href={getAdminUrl(`series/${typeId}`)} textDecor="none" width="100%">
                    <MenuItem
                      value="adminSeriesPage"
                      icon={faCog}
                      label="Series Page (Admin Only)"
                    />
                  </ShwLink>
                ) : (
                  <ShwLink href={getAdminUrl(`session/${typeId}`)} textDecor="none" width="100%">
                    <MenuItem
                      value="adminSessionPage"
                      icon={faCog}
                      label="Session Page (Admin Only)"
                    />
                  </ShwLink>
                )}
              </Box>
            </>
          ) : null}
        </MenuList>
      </Menu>
      {/* TODO: allow reporting of co-hosts */}
      <ReportModalV2
        entity={{ type: `${type}-host`, id: mainHostId ?? 0 }}
        open={showReportModal}
        onClose={() => setShowReportModal(false)}
        reportedUserId={mainHostUserId}
        reportedUserName={mainHostName}
      />
      <CohostRequestModal
        subjectUserId={currentUser.id}
        sessionOrSeriesId={typeId}
        isSession={isSession}
      />
      <CohostCancelRequestModal hostId={hostId} isSession={isSession} />
      <CohostRemoveSelfModal hostId={hostId} mainHostId={tutorId} isSession={isSession} />

      <ModalV3
        open={showTableModal}
        onClose={() => {
          setShowTableModal(false)
        }}
        title="Hosts"
        size="xl"
        body={() => (
          <HostTable
            hosts={hosts}
            canRemoveHosts={canRemoveCohosts}
            setRemoveCohostInfo={setRemoveCohostInfo}
            setShowTableModal={setShowTableModal}
          />
        )}
      />
      <ModalV3
        title="Remove Co-host"
        size="xl"
        open={removeCohostInfo != null}
        onClose={() => {
          setRemoveCohostInfo(null)
          setShowTableModal(true)
        }}
        body={() => (
          <>
            <Text>
              Are you sure you want to remove {removeCohostInfo?.name} as a co-host? They will need
              to request again in order to become a co-host again.
            </Text>
            <Form name="CohostRemove" blocks={removeFormInputs} onSubmit={handleRemove} />
          </>
        )}
      />
    </>
  )
}

export type MoreOptionsButtonSeriesData = Pick<
  SeriesPageData,
  'allowCohosts' | 'hosts' | 'isClosed' | 'viewer' | 'world'
>

export type MoreOptionsDetails = {
  sessionStartDate: string
  allowsCohosting: boolean
  isSeriesSession: boolean
  hasSeriesEnded: boolean
  isHost: boolean
  requestedHost: boolean
  hostId: number
  mainHostId: number
  mainHostUserId: number
  mainHostName: string
  worldId: number | null
  worldTitle: string
  isTutorInSubworld: boolean
  hosts: HostAvatarDetails[]
}

export function moreOptionsDetailsFromSeriesData(
  seriesData: MoreOptionsButtonSeriesData
): MoreOptionsDetails {
  const mainHost = seriesData.hosts.find(host => host.role === 'main') ?? null

  return {
    sessionStartDate: '',
    allowsCohosting: seriesData.allowCohosts,
    isSeriesSession: false,
    hasSeriesEnded: seriesData.isClosed,
    isHost: seriesData.viewer.isApprovedHost,
    requestedHost: seriesData.viewer.isHost && !seriesData.viewer.isApprovedHost,
    hostId: seriesData.viewer.seriesHostId ?? 0,
    mainHostId: mainHost?.seriesHostId ?? 0,
    mainHostUserId: mainHost?.userId ?? 0,
    mainHostName: mainHost?.displayName ?? '',
    worldId: seriesData.world?.id ?? null,
    worldTitle: seriesData.world?.title ?? '',
    isTutorInSubworld: seriesData.viewer.isTutorInWorld,
    hosts: seriesData.hosts.map(host =>
      hostAvatarDetailsFromData(host, { isSession: false, hostId: host.seriesHostId })
    ),
  }
}
